import { Controller } from '@hotwired/stimulus';
import Chart from 'chart.js/auto';

const centerTextPlugin = {
  id: 'dashboardCenterText',
  afterDraw(chart) {
    const opts = chart.config.options.centerLabel;
    if (!opts) return;
    const ca = chart.chartArea;
    if (!ca) return;
    const cx = (ca.left + ca.right) / 2;
    const cy = (ca.top + ca.bottom) / 2;
    const { ctx } = chart;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    let rows;
    if (opts.monthlyTargetMet) {
      rows = [
        { value: 'Monthly target met', brand: true },
        { label: 'Actual', value: opts.actual, big: true },
      ];
      if (opts.showMetDelta) rows.push({ label: 'Exceeded By', value: opts.todo });
    } else {
      rows = [{ label: 'Actual', value: opts.actual, big: true }];
      // With no target configured there is nothing to be ahead/behind of, so omit the
      // Exceeded By / To Do line rather than showing a meaningless "Exceeded By £0".
      if (opts.targetConfigured) {
        rows.push({ label: opts.exceeded ? 'Exceeded By' : 'To Do', value: opts.todo });
      }
      rows.push({ label: 'Target', value: opts.target });
    }
    const chartWidth = ca.right - ca.left;
    const scale = chartWidth / 200;
    const bigSize = Math.max(12, Math.round(20 * scale));
    const brandSize = Math.max(9, Math.round(11 * scale));
    const labelSize = Math.max(7, Math.round(10 * scale));
    const valueSize = Math.max(9, Math.round(12 * scale));
    const rowHeights = rows.map((r) => {
      if (r.brand) return Math.round(26 * scale);
      return r.big ? Math.round(30 * scale) : Math.round(32 * scale);
    });
    const totalH = rowHeights.reduce((a, b) => a + b, 0);
    let y = cy - totalH / 2;
    rows.forEach((r, i) => {
      const h = rowHeights[i];
      if (r.brand) {
        ctx.font = `700 ${brandSize}px Inter, sans-serif`;
        ctx.fillStyle = 'rgb(184,27,88)';
        ctx.fillText(r.value, cx, y + Math.round(h * 0.75));
      } else if (r.big) {
        ctx.font = `700 ${bigSize}px Inter, sans-serif`;
        ctx.fillStyle = 'rgb(31,4,15)';
        ctx.fillText(r.value, cx, y + Math.round(h * 0.87));
      } else {
        ctx.font = `500 ${labelSize}px Inter, sans-serif`;
        ctx.fillStyle = 'rgba(31,4,15,0.4)';
        ctx.fillText(r.label.toUpperCase(), cx, y + Math.round(h * 0.38));
        ctx.font = `600 ${valueSize}px Inter, sans-serif`;
        ctx.fillStyle = 'rgba(31,4,15,0.7)';
        ctx.fillText(r.value, cx, y + Math.round(h * 0.88));
      }
      y += h;
    });
    ctx.restore();
  },
};

function ragColor(actual, target) {
  const pct = target > 0 ? (actual / target) * 100 : 100;
  if (pct < 50) return 'rgb(220,37,37)';
  if (pct < 75) return 'rgb(255,111,27)';
  if (pct < 100) return 'rgb(17,160,70)';
  return 'rgb(29,93,234)';
}

export default class extends Controller {
  static values = {
    actual: Number,
    todo: Number,
    actualLabel: String,
    todoLabel: String,
    targetLabel: String,
    exceeded: Boolean,
    monthlyTargetMet: Boolean,
    showMetDelta: Boolean,
    targetConfigured: Boolean,
  };

  connect() {
    const target = this.actualValue + Math.max(this.todoValue, 0);
    const color = ragColor(this.actualValue, target);
    const isComplete = this.actualValue > 0 && this.todoValue <= 0;
    // With no income and no remaining target the data would be [0, 0], which Chart.js
    // draws as nothing; fall back to a faint full track so the empty ring is still visible.
    const remainder = this.actualValue <= 0 && this.todoValue <= 0 ? 1 : this.todoValue;

    this.chart = new Chart(this.element, {
      type: 'doughnut',
      data: {
        datasets: [
          {
            data: isComplete ? [1] : [this.actualValue, remainder],
            backgroundColor: isComplete ? [color] : [color, 'rgba(31,4,15,0.05)'],
            borderWidth: 0,
            borderRadius: isComplete ? 0 : 3,
          },
        ],
      },
      options: {
        cutout: '74%',
        responsive: true,
        maintainAspectRatio: true,
        animation: { duration: 700, easing: 'easeOutQuart' },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
        },
        centerLabel: {
          actual: this.actualLabelValue,
          todo: this.todoLabelValue,
          target: this.targetLabelValue,
          exceeded: this.exceededValue,
          monthlyTargetMet: this.monthlyTargetMetValue,
          showMetDelta: this.showMetDeltaValue,
          targetConfigured: this.targetConfiguredValue,
        },
      },
      plugins: [centerTextPlugin],
    });
  }

  disconnect() {
    this.chart?.destroy();
  }
}
